<template>
    <NotificationModalShell
        additional-class-name="SetNotificationEnablementModal"
        :open="open"
        :submitting="submitting"
        :save-disabled="!changed || submitting"
        :title="title"
        @close="$emit('close')"
        @submit="onSubmit"
    >
        <NotificationMasterSwitch :value="enabled" @input="setEnabled" />

        <p>{{ description }}</p>

        <NotificationsOffNote v-if="!enabled" />
    </NotificationModalShell>
</template>

<script lang="ts">
    import { defineComponent } from 'vue';

    import {
        NotificationType,
        ReminderlessNotificationType,
    } from '@evidentid/tprm-portal-lib/models/notification-configuration';

    import NotificationMasterSwitch from './NotificationMasterSwitch.vue';
    import NotificationModalShell from './NotificationModalShell.vue';
    import NotificationsOffNote from './NotificationsOffNote.vue';

    interface NotificationCopy {
        title: string;
        on: string;
        off: string;
    }

    // Every reminder-less type needs an entry; Record (not Partial) makes a missing one a
    // compile error rather than a blank modal.
    const copy: Record<ReminderlessNotificationType, NotificationCopy> = {
        [NotificationType.compliant]: {
            title: 'Compliant Status Notification',
            on: 'An email is sent automatically when an entity becomes compliant with your requirements. There are no reminders for this notification.',
            off: 'Entities will not receive an email when they become compliant with your requirements.',
        },
    };

    export default defineComponent({
        name: 'SetNotificationEnablementModal',
        components: {
            NotificationModalShell,
            NotificationMasterSwitch,
            NotificationsOffNote,
        },
        props: {
            open: {
                type: Boolean,
                default: false,
            },
            submitting: {
                type: Boolean,
                default: false,
            },
            type: {
                type: String as () => ReminderlessNotificationType,
                required: true,
            },
            initialValue: {
                type: Boolean,
                default: false,
            },
        },
        emits: [ 'close', 'submit' ],
        data() {
            return {
                enabled: this.initialValue,
            };
        },
        computed: {
            changed(): boolean {
                return this.enabled !== this.initialValue;
            },
            title(): string {
                return copy[this.type].title;
            },
            description(): string {
                return this.enabled ? copy[this.type].on : copy[this.type].off;
            },
        },
        watch: {
            initialValue(value: boolean, previous: boolean): void {
                // A reload while the modal is open must not discard a choice the user has made
                // but not yet saved, so only adopt the new value if they had not diverged from
                // the one it replaces.
                if (this.enabled === previous) {
                    this.enabled = value;
                }
            },
        },
        methods: {
            setEnabled(value: boolean): void {
                this.enabled = value;
            },
            onSubmit(): void {
                if (this.submitting || !this.changed) {
                    return;
                }
                this.$emit('submit', this.enabled);
            },
        },
    });
</script>
